<script setup>
import { formatNumber, formatRupiah } from '@/composables/useFormat';
import { useApi } from '@/utils/api'; // ← GANTI dari $api ke useApi
import { useAbility } from '@casl/vue';

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Dashboard',
  },
})

const ability = useAbility()

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(false)
const selectedDate = ref(new Date().toISOString().slice(0, 10))

const sales    = ref({ total_orders: 0, revenue: 0, hpp: 0, gross_profit: 0, total_discount: 0, total_tax: 0 })
const expenses = ref({ total: 0 })
const topProducts = ref([])
const lowStock    = ref([])
const shift       = ref(null)

// ── Fetch ──────────────────────────────────────────────────────────────────────
const fetchDashboard = async () => {
  loading.value = true
  try {
    const response = await useApi('/v1/reports/dashboard', {
      query: { date: selectedDate.value },
    }).get()
    
    const data = response?.data ?? response

    sales.value       = data?.sales       ?? sales.value
    expenses.value    = data?.expenses    ?? expenses.value
    topProducts.value = data?.top_products ?? []
    lowStock.value    = data?.low_stock     ?? []
    shift.value       = data?.shift        ?? null
  } catch (err) {
    console.error('❌ Dashboard error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchDashboard)
watch(selectedDate, fetchDashboard)

// ── Computed ──────────────────────────────────────────────────────────────────
const netProfit = computed(() => sales.value.gross_profit - expenses.value.total)

const grossMargin = computed(() => {
  if (!sales.value.revenue) return 0
  return ((sales.value.gross_profit / sales.value.revenue) * 100).toFixed(1)
})

const statCards = computed(() => [
  {
    label: 'Omzet Hari Ini',
    value: formatRupiah(sales.value.revenue),
    icon: 'ri-money-dollar-circle-line',
    color: 'primary',
  },
  {
    label: 'Total Transaksi',
    value: formatNumber(sales.value.total_orders),
    icon: 'ri-shopping-cart-line',
    color: 'success',
  },
  {
    label: 'Laba Kotor',
    value: formatRupiah(sales.value.gross_profit),
    icon: 'ri-line-chart-line',
    color: 'info',
    sub: `Margin ${grossMargin.value}%`,
  },
  {
    label: 'Stok Kritis',
    value: formatNumber(lowStock.value.length),
    icon: 'ri-archive-line',
    color: lowStock.value.length > 0 ? 'error' : 'success',
    sub: lowStock.value.length > 0 ? 'Perlu restok segera' : 'Stok aman',
  },
])

const avatarColors = ['primary', 'success', 'info', 'warning', 'error']
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">Dashboard</h4>
        <p class="text-body-2 text-medium-emphasis">Ringkasan performa toko</p>
      </VCol>
      <VCol cols="12" md="3" offset-md="3">
        <VTextField
          v-model="selectedDate"
          type="date"
          label="Tanggal"
          density="compact"
          hide-details
          :loading="loading"
        />
      </VCol>
    </VRow>

    <!-- Shift Banner -->
    <VAlert
      v-if="shift"
      type="info"
      variant="tonal"
      class="mb-4"
      icon="ri-time-line"
    >
      Shift sedang buka sejak {{ shift.opened_at ? new Date(shift.opened_at).toLocaleTimeString('id-ID') : '-' }}
      · Kas awal: {{ formatRupiah(shift.opening_cash) }}
    </VAlert>

    <!-- Stat Cards -->
    <VRow class="mb-4">
      <VCol
        v-for="card in statCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <VCard :loading="loading">
          <VCardText>
            <div class="d-flex align-center mb-3">
              <VAvatar :color="card.color" variant="tonal" size="44">
                <VIcon :icon="card.icon" size="22" />
              </VAvatar>
            </div>
            <div class="text-h5 font-weight-bold mb-1">{{ card.value }}</div>
            <div class="text-body-2 text-medium-emphasis">{{ card.label }}</div>
            <div v-if="card.sub" class="text-caption mt-1" :class="`text-${card.color}`">
              {{ card.sub }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Detail Row -->
    <VRow>
      <!-- Breakdown Keuangan -->
      <VCol cols="12" md="4">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
            Ringkasan Keuangan
          </VCardTitle>
          <VDivider />
          <VList density="compact">
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-arrow-up-circle-line" color="success" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Omzet</VListItemTitle>
              <template #append>
                <span class="text-body-2 font-weight-medium text-success">
                  {{ formatRupiah(sales.revenue) }}
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="warning" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Diskon</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-warning">
                  ({{ formatRupiah(sales.total_discount) }})
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="warning" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">HPP</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-warning">
                  ({{ formatRupiah(sales.hpp) }})
                </span>
              </template>
            </VListItem>
            <VDivider class="my-1" />
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-line-chart-line" color="info" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2 font-weight-semibold">Laba Kotor</VListItemTitle>
              <template #append>
                <span class="text-body-2 font-weight-bold text-info">
                  {{ formatRupiah(sales.gross_profit) }}
                </span>
              </template>
            </VListItem>
            <VListItem>
              <template #prepend>
                <VIcon icon="ri-subtract-line" color="error" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">Pengeluaran</VListItemTitle>
              <template #append>
                <span class="text-body-2 text-error">
                  ({{ formatRupiah(expenses.total) }})
                </span>
              </template>
            </VListItem>
            <VDivider class="my-1" />
            <VListItem>
              <template #prepend>
                <VIcon
                  :icon="netProfit >= 0 ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'"
                  :color="netProfit >= 0 ? 'success' : 'error'"
                  size="18"
                  class="mr-2"
                />
              </template>
              <VListItemTitle class="text-body-2 font-weight-bold">Laba Bersih</VListItemTitle>
              <template #append>
                <span
                  class="text-body-2 font-weight-bold"
                  :class="netProfit >= 0 ? 'text-success' : 'text-error'"
                >
                  {{ formatRupiah(netProfit) }}
                </span>
              </template>
            </VListItem>
          </VList>
        </VCard>
      </VCol>

      <!-- Top Produk -->
      <VCol cols="12" md="4">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
            Top 5 Produk
          </VCardTitle>
          <VDivider />

          <!-- Empty state -->
          <VCardText v-if="!loading && topProducts.length === 0" class="text-center py-8">
            <VIcon icon="ri-cup-line" size="48" color="secondary" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">Belum ada transaksi hari ini</p>
          </VCardText>

          <VList v-else density="compact">
            <VListItem
              v-for="(item, i) in topProducts"
              :key="i"
            >
              <template #prepend>
                <VAvatar
                  :color="avatarColors[i] || 'secondary'"
                  variant="tonal"
                  size="32"
                  class="text-caption font-weight-bold mr-3"
                >
                  {{ i + 1 }}
                </VAvatar>
              </template>
              <VListItemTitle class="text-body-2">
                {{ item.product_name }}
                <span v-if="item.variant_name" class="text-medium-emphasis"> · {{ item.variant_name }}</span>
              </VListItemTitle>
              <VListItemSubtitle>{{ formatNumber(item.total_qty) }} terjual</VListItemSubtitle>
              <template #append>
                <span class="text-body-2 text-success font-weight-medium">
                  {{ formatRupiah(item.total_revenue) }}
                </span>
              </template>
            </VListItem>
          </VList>
        </VCard>
      </VCol>

      <!-- Low Stock Alert -->
      <VCol cols="12" md="4">
        <VCard :loading="loading" height="100%">
          <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2 d-flex align-center">
            Stok Kritis
            <VChip
              v-if="lowStock.length > 0"
              color="error"
              size="small"
              class="ml-2"
            >
              {{ lowStock.length }}
            </VChip>
          </VCardTitle>
          <VDivider />

          <!-- Empty / all ok -->
          <VCardText v-if="!loading && lowStock.length === 0" class="text-center py-8">
            <VIcon icon="ri-checkbox-circle-line" size="48" color="success" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">Semua stok aman</p>
          </VCardText>

          <VList v-else density="compact">
            <VListItem
              v-for="item in lowStock"
              :key="item.id"
            >
              <template #prepend>
                <VIcon icon="ri-error-warning-line" color="error" size="18" class="mr-2" />
              </template>
              <VListItemTitle class="text-body-2">{{ item.name }}</VListItemTitle>
              <VListItemSubtitle class="text-caption text-error">
                Stok: {{ item.current_stock }} {{ item.unit }} (min {{ item.minimum_stock }})
              </VListItemSubtitle>
            </VListItem>
          </VList>

          <VCardActions v-if="lowStock.length > 0">
            <VBtn
              v-if="ability.can('create', 'Purchase')"
              :to="{ name: 'purchase-orders-create' }"
              color="error"
              variant="tonal"
              size="small"
              prepend-icon="ri-shopping-bag-line"
              block
            >
              Buat Purchase Order
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
